// Settings > Dispatch > Shipping Rules — the ordered, per-warehouse
// rule list that decides which carrier service an order books with
// when its LAST item is scanned at the pack bench. First match wins;
// no match (or a tariff violation) blocks the order — nothing ships
// without a booked label.
//
// Data:
//   GET/POST/PATCH/DELETE /dispatch-admin/shipping-rules
//   POST /dispatch-admin/shipping-rules/reorder { warehouse, ordered_ids }
//   POST /dispatch-admin/shipping-rules/dry-run   (inline rule tester)
//   GET  /admin/carriers                          (service dropdown options)

import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, FlaskConical, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { useReadOnly } from "../../../access/ReadOnly";
import { useWarehouseScope } from "../../../access/useWarehouseScope";
import { apiFetch } from "../../../api/apiFetch";
import type { CarrierConfig, CarrierService } from "../integrations/types";
import "../../../styles/admin-integrations.css";

interface RuleConditions {
    delivery_types?: string[];
    box_names?: string[];
    min_weight_grams?: number;
    max_weight_grams?: number;
    min_boxes?: number;
    max_boxes?: number;
    max_longest_side_cm?: number;
    max_length_cm?: number;
    max_width_cm?: number;
    max_height_cm?: number;
    countries?: string[];
    uk_regions?: string[];
    postcode_includes?: string[];
    postcode_excludes?: string[];
    // Sales channels as the orders table spells them ("Bodysocks UK");
    // matched trim + case-insensitive by the evaluator.
    sales_channels?: string[];
    // Inclusive YYYY-MM-DD window of dispatch days (Europe/London). A
    // one-day routing change (Halloween 2026: Bodysocks → InPost) expires
    // itself instead of waiting for someone to switch it off.
    date_from?: string;
    date_to?: string;
}

// Dispatch-box catalogue entry (subset of /dispatch-admin/boxes) — the
// box_names condition and the tester's box-type picker key on `name`.
interface WarehouseBox {
    name: string;
    length_mm: number | null;
    width_mm: number | null;
    height_mm: number | null;
    weight_grams: number | null;
}

type RuleStrategy = "fixed" | "cheapest";

interface ShippingRule {
    id: number;
    warehouse: string;
    position: number;
    name: string;
    enabled: boolean;
    conditions: RuleConditions;
    // 'fixed' books carrierServiceId; 'cheapest' prices every
    // candidateServiceIds entry on the whole shipment and books the
    // winner (migration 185).
    strategy: RuleStrategy;
    carrierServiceId: number | null;
    candidateServiceIds: number[];
}

interface DryRunCandidate {
    serviceId: number;
    serviceCode: string;
    carrier: string;
    cost: number | null;
    excluded: string | null;
    consignments?: number;
    surcharges?: { label: string; cost: number }[];
    // Which lane (rule) contributed the candidate — cheapest lanes pool.
    ruleId?: number;
    ruleName?: string;
    fixed?: boolean;
    // InPost: extra transit days their Remote Areas table publishes for
    // the destination (Express is still labelled "HOME 24" there).
    transit?: { area: string; extraDays: number; days: string } | null;
}

interface ServiceOption extends CarrierService {
    carrierName: string;
    accountWarehouse: string;
}

interface DryRunResult {
    matched: boolean;
    rule?: { id: number; name: string; strategy?: RuleStrategy } | null;
    service?: {
        id?: number;
        code: string;
        name: string;
        carrier?: string;
        estimatedCost?: number | null;
        surcharges?: { label: string; cost: number }[];
        consignments?: number;
        breakdown?: { piece?: number; band?: string; kg?: number; cost?: number }[] | null;
        transit?: { area: string; extraDays: number; days: string } | null;
    } | null;
    candidates?: DryRunCandidate[] | null;
    reason?: string;
    trace?: { ruleId: number; name: string; failed: string; candidates?: DryRunCandidate[] }[];
    facts?: {
        totalWeightGrams?: number;
        chargeableWeightGrams?: number;
        ukRegion?: string | null;
        boxCount?: number;
        inpostBands?: (string | null)[];
    };
}

const DELIVERY_TYPES = ["standard", "express", "priority"] as const;

// Region slugs mirror UK_REGIONS in the BE evaluator
// (app/warehouse/services/shippingRules.js) — keep in sync.
const UK_REGIONS = [
    "england",
    "wales",
    "scotland",
    "scottish_highlands",
    "scottish_islands",
    "northern_ireland",
    "isle_of_man",
    "isle_of_wight",
    "isles_of_scilly",
    "channel_islands",
] as const;

// "APC Overnight · ND16 — Next Day (£4.44, +£0.25/kg over 20kg)" — the
// carrier name leads so multi-carrier warehouses stay unambiguous, and
// the tariff is what lets the admin order rules by cost. Per-parcel
// cost only shows when it's actually charged (>0) — ND/ROAD bill one
// base rate per consignment, so their "+£0.00/parcel" was noise; the
// per-kg escalator above the included allowance is what matters there
// (per Jack, 2026-08-05).
const serviceOptionLabel = (s: ServiceOption): string => {
    // Per-parcel band pricing (InPost): show the bands, not a base rate.
    if (s.pricing?.bands || s.pricing?.flat != null) {
        const p = s.pricing;
        const bands = p.bands
            ? (["S", "M", "L"] as const).filter((b) => p.bands?.[b] != null).map((b) => `${b} £${Number(p.bands?.[b]).toFixed(2)}`).join(" / ")
            : `£${Number(p.flat).toFixed(2)} flat`;
        return `${s.carrierName} · ${s.serviceCode} — ${s.displayName} (${bands}/parcel + surcharges)`;
    }
    if (s.baseCost == null) return `${s.carrierName} · ${s.serviceCode} — ${s.displayName}`;
    const parts = [`£${s.baseCost.toFixed(2)}`];
    if (s.additionalParcelCost != null && s.additionalParcelCost > 0) {
        parts.push(`+£${s.additionalParcelCost.toFixed(2)}/parcel`);
    }
    if (s.perKgCost != null && s.perKgCost > 0) {
        parts.push(`+£${s.perKgCost.toFixed(2)}/kg${s.includedKg != null ? ` over ${s.includedKg}kg` : ""}`);
    }
    return `${s.carrierName} · ${s.serviceCode} — ${s.displayName} (${parts.join(", ")})`;
};

const isPalletService = (s: { itemType?: string }): boolean => s.itemType === "PALLET";

// Dropdown / tooltip label: the tariff label, plus a "· pallets" suffix on
// pallet services so they are unmistakable (they route PALLET orders at the
// pallet desk, not parcels at the pack bench).
const serviceLabel = (s: ServiceOption, t: (key: string) => string): string =>
    isPalletService(s) ? `${serviceOptionLabel(s)} ${t("shipping_rules_service_pallet_suffix")}` : serviceOptionLabel(s);

const jsonFetch = async (path: string, method: string = "GET", body?: unknown) => {
    const res = await apiFetch(path, {
        method,
        ...(body === undefined
            ? {}
            : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
    return json;
};

// Comma/space separated pattern list ↔ array.
const parsePatterns = (raw: string): string[] =>
    raw.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean);

// Comma-separated channel names ↔ array. Channel names carry spaces
// ("Bodysocks UK") and their own casing, so only the comma splits.
const parseChannels = (raw: string): string[] =>
    [...new Set(raw.split(",").map(s => s.trim()).filter(Boolean))];

// The evaluator derives UK region FROM the postcode, so the tester's
// region picker works by filling a representative example postcode —
// visible and editable, and postcode include/exclude rules keep seeing
// a real postcode. Keep in sync with ukRegionForPostcode (BE).
const REGION_EXAMPLE_POSTCODE: Record<string, string> = {
    england: "B1 1AA",
    wales: "CF10 1AA",
    scotland: "EH1 1AA",
    scottish_highlands: "IV1 1AA",
    scottish_islands: "HS1 1AA",
    northern_ireland: "BT1 1AA",
    isle_of_man: "IM1 1AA",
    isle_of_wight: "PO30 1AA",
    isles_of_scilly: "TR21 1AA",
    channel_islands: "JE1 1AA",
};

interface TesterBox {
    name: string; // dispatch-box type ("P1"); "" = custom dims
    weightKg: string;
    lengthCm: string;
    widthCm: string;
    heightCm: string;
}

const DEFAULT_TESTER_BOX: TesterBox = { name: "", weightKg: "1", lengthCm: "30", widthCm: "20", heightCm: "10" };

// One rule row: enabled toggle, name, condition editor, target service,
// delete. Numeric/text fields commit on blur; toggles immediately.
const RuleRow = ({
    rule,
    services,
    boxNames,
    onChanged,
    onDeleted,
    onMove,
    isFirst,
    isLast,
}: {
    rule: ShippingRule;
    services: ServiceOption[];
    boxNames: string[];
    onChanged: (next: ShippingRule) => void;
    onDeleted: (id: number) => void;
    onMove: (id: number, dir: -1 | 1) => void;
    isFirst: boolean;
    isLast: boolean;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    // Size/weight filters start open only when the rule uses one.
    const [advancedOpen, setAdvancedOpen] = useState(() =>
        (["min_weight_grams", "max_weight_grams", "min_boxes", "max_boxes",
            "max_longest_side_cm", "max_length_cm", "max_width_cm", "max_height_cm"] as (keyof RuleConditions)[])
            .some(k => rule.conditions[k] !== undefined),
    );

    const patch = async (body: Partial<{
        name: string; enabled: boolean; conditions: RuleConditions;
        carrierServiceId: number | null; strategy: RuleStrategy; candidateServiceIds: number[] | null;
    }>) => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const json = await jsonFetch(`/dispatch-admin/shipping-rules/${rule.id}`, "PATCH", body);
            onChanged(json.rule as ShippingRule);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const patchCondition = (key: keyof RuleConditions, value: RuleConditions[keyof RuleConditions] | undefined) => {
        const next: RuleConditions = { ...rule.conditions };
        if (value === undefined || (Array.isArray(value) && value.length === 0)) {
            delete next[key];
        } else {
            (next as Record<string, unknown>)[key] = value;
        }
        patch({ conditions: next });
    };

    const numBlur = (key: keyof RuleConditions, scale = 1) =>
        (e: React.FocusEvent<HTMLInputElement>) => {
            const raw = e.target.value.trim();
            const current = rule.conditions[key] as number | undefined;
            if (raw === "") {
                if (current !== undefined) patchCondition(key, undefined);
                return;
            }
            const n = Math.round(Number(raw) * scale);
            if (!Number.isFinite(n) || n < 0) return;
            if (n === current) return;
            patchCondition(key, n);
        };

    const toggleDeliveryType = (dt: string) => {
        const current = rule.conditions.delivery_types ?? [];
        const next = current.includes(dt) ? current.filter(d => d !== dt) : [...current, dt];
        patchCondition("delivery_types", next.length > 0 ? next : undefined);
    };

    const toggleUkRegion = (region: string) => {
        const current = rule.conditions.uk_regions ?? [];
        const next = current.includes(region) ? current.filter(r => r !== region) : [...current, region];
        patchCondition("uk_regions", next.length > 0 ? next : undefined);
    };

    const toggleBoxName = (name: string) => {
        const current = rule.conditions.box_names ?? [];
        const next = current.includes(name) ? current.filter(n => n !== name) : [...current, name];
        patchCondition("box_names", next.length > 0 ? next : undefined);
    };

    const remove = async () => {
        if (readOnly) return;
        setSaving(true);
        try {
            await jsonFetch(`/dispatch-admin/shipping-rules/${rule.id}`, "DELETE");
            onDeleted(rule.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setConfirmingDelete(false);
            setSaving(false);
        }
    };

    const dt = rule.conditions.delivery_types ?? [];

    // The target service's tariff limits (Carriers → Services) are
    // INHERITED conditions — enforced automatically at booking with
    // fall-through to the next rule. Shown read-only here so nobody
    // re-types them into the condition fields below (per Jack,
    // 2026-08-06: "at the moment it is duplicated").
    const isCheapest = rule.strategy === "cheapest";
    const selectedService = isCheapest ? undefined : services.find(s => s.id === rule.carrierServiceId);
    // "Cheapest" is parcel-only: the BE ignores pallet candidates.
    const parcelServices = services.filter(s => !isPalletService(s));
    const hasPalletService = parcelServices.length < services.length;
    const candidateSet = new Set(rule.candidateServiceIds ?? []);
    const toggleCandidate = (id: number) => {
        const next = candidateSet.has(id)
            ? (rule.candidateServiceIds ?? []).filter(x => x !== id)
            : [...(rule.candidateServiceIds ?? []), id];
        patch({ candidateServiceIds: next });
    };
    const serviceLimits = (() => {
        if (!selectedService) return "";
        const parts: string[] = [];
        if (selectedService.maxPieces != null) {
            parts.push(t("shipping_rules_limit_boxes", { n: String(selectedService.maxPieces) }));
        }
        if (selectedService.maxWeightGrams != null) {
            parts.push(t("shipping_rules_limit_weight", { kg: String(selectedService.maxWeightGrams / 1000) }));
        }
        const dims = [selectedService.maxLengthCm, selectedService.maxWidthCm, selectedService.maxHeightCm]
            .filter((v): v is number => v != null);
        if (dims.length > 0) {
            parts.push(t("shipping_rules_limit_dims", { dims: dims.join("×") }));
        }
        return parts.join(" · ");
    })();

    return (
        <details className="shipping-rule-row">
            <summary className="shipping-rule-summary">
                <span className="shipping-rule-order-btns">
                    <button type="button" className="btn btn-sm" disabled={saving || isFirst || readOnly} title={readOnly ? t("read_only_mode") : undefined} onClick={(e) => { e.preventDefault(); onMove(rule.id, -1); }} aria-label={t("shipping_rules_move_up")}>
                        <ArrowUp size={14} />
                    </button>
                    <button type="button" className="btn btn-sm" disabled={saving || isLast || readOnly} title={readOnly ? t("read_only_mode") : undefined} onClick={(e) => { e.preventDefault(); onMove(rule.id, 1); }} aria-label={t("shipping_rules_move_down")}>
                        <ArrowDown size={14} />
                    </button>
                </span>
                <span className="shipping-rule-title">{rule.position + 1}. {rule.name}</span>
                <span className="shipping-rule-service">
                    {isCheapest
                        ? t("shipping_rules_strategy_cheapest_summary", { n: String(candidateSet.size) })
                        : (services.find(s => s.id === rule.carrierServiceId)?.serviceCode ?? `#${rule.carrierServiceId}`)}
                </span>
                <span className={`admin-integrations-status-pill admin-integrations-status-pill--${rule.enabled ? "ok" : "off"}`}>
                    {rule.enabled ? t("integrations_status_pill_enabled") : t("integrations_status_pill_disabled")}
                </span>
            </summary>
            <div className="shipping-rule-body">
                <div className="admin-integrations-form-row--toggle">
                    <label className="admin-settings-toggle">
                        <input type="checkbox" checked={rule.enabled} disabled={saving || readOnly} onChange={(e) => patch({ enabled: e.target.checked })} />
                        <span className="admin-settings-slider" />
                    </label>
                    <span className="admin-integrations-toggle-label">{t("shipping_rules_enabled_toggle")}</span>
                </div>

                <div className="shipping-rule-grid">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_name")}</label>
                        <input
                            className="admin-integrations-input"
                            defaultValue={rule.name}
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v && v !== rule.name) patch({ name: v });
                            }}
                        />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_strategy")}</label>
                        <div className="admin-integrations-mode-toggle">
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn${!isCheapest ? " admin-integrations-mode-btn--active" : ""}`}
                                disabled={saving || !isCheapest}
                                onClick={() => patch({ strategy: "fixed", carrierServiceId: rule.candidateServiceIds?.[0] ?? services[0]?.id ?? null })}
                            >
                                {t("shipping_rules_strategy_fixed")}
                            </button>
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn${isCheapest ? " admin-integrations-mode-btn--active" : ""}`}
                                disabled={saving || isCheapest || parcelServices.length < 2}
                                onClick={() => {
                                    // Seed the candidate list with the current service plus the
                                    // next one so the DB check (2+ candidates) holds.
                                    const first = parcelServices.find(s => s.id === rule.carrierServiceId)?.id ?? parcelServices[0]?.id;
                                    const second = parcelServices.find(s => s.id !== first)?.id;
                                    patch({ strategy: "cheapest", candidateServiceIds: [first, second].filter((x): x is number => x != null) });
                                }}
                            >
                                {t("shipping_rules_strategy_cheapest")}
                            </button>
                        </div>
                    </div>
                    {!isCheapest && (
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label">{t("shipping_rules_target_service")}</label>
                            <select
                                className="admin-integrations-input"
                                value={rule.carrierServiceId ?? ""}
                                disabled={saving || readOnly}
                                onChange={(e) => patch({ carrierServiceId: Number(e.target.value) })}
                            >
                                {services.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {serviceLabel(s, t)}
                                    </option>
                                ))}
                            </select>
                            {selectedService && isPalletService(selectedService) && (
                                <p className="admin-integrations-field-hint">{t("shipping_rules_pallet_rule_hint")}</p>
                            )}
                        </div>
                    )}
                </div>
                {isCheapest && (
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_candidates")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_candidates_hint")}</p>
                        {hasPalletService && <p className="admin-integrations-field-hint">{t("shipping_rules_cheapest_parcel_only")}</p>}
                        <div className="admin-integrations-day-chips">
                            {parcelServices.map(s => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={`admin-integrations-day-chip${candidateSet.has(s.id) ? " admin-integrations-day-chip--active" : ""}`}
                                    disabled={saving || readOnly || (candidateSet.has(s.id) && candidateSet.size <= 2)}
                                    onClick={() => toggleCandidate(s.id)}
                                    title={serviceLabel(s, t)}
                                >
                                    {s.carrierName} · {s.serviceCode}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {serviceLimits && (
                    <p className="admin-integrations-field-hint">
                        {t("shipping_rules_service_limits", {
                            code: selectedService?.serviceCode ?? "",
                            limits: serviceLimits,
                        })}
                    </p>
                )}

                <label className="admin-integrations-field-label">{t("shipping_rules_conditions")}</label>
                <p className="admin-integrations-field-hint">{t("shipping_rules_conditions_hint")}</p>

                <div className="admin-integrations-field">
                    <span className="admin-integrations-field-hint">{t("shipping_rules_delivery_types")}</span>
                    <div className="admin-integrations-day-chips">
                        {DELIVERY_TYPES.map(d => (
                            <button
                                key={d}
                                type="button"
                                className={`admin-integrations-day-chip${dt.includes(d) ? " admin-integrations-day-chip--active" : ""}`}
                                disabled={saving || readOnly}
                                onClick={() => toggleDeliveryType(d)}
                            >
                                {t(`delivery_type_${d}`)}
                            </button>
                        ))}
                    </div>
                </div>

                {boxNames.length > 0 && (
                    <div className="admin-integrations-field">
                        <span className="admin-integrations-field-label">{t("shipping_rules_box_names")}</span>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_box_names_hint")}</p>
                        <div className="admin-integrations-day-chips shipping-rule-region-chips">
                            {boxNames.map(name => (
                                <button
                                    key={name}
                                    type="button"
                                    className={`admin-integrations-day-chip${(rule.conditions.box_names ?? []).includes(name) ? " admin-integrations-day-chip--active" : ""}`}
                                    disabled={saving || readOnly}
                                    onClick={() => toggleBoxName(name)}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Size/weight routing filters — collapsed by default
                    because the service's own limits already apply
                    automatically (per Jack, 2026-08-06); these exist to
                    STEER shipments between services (e.g. weight-tier a
                    cheaper carrier), not to gate what a service can carry.
                    Opens itself when a rule actually uses one. */}
                <details
                    className="admin-integrations-vendors-details"
                    open={advancedOpen}
                    onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}
                >
                    <summary className="admin-integrations-vendors-summary">
                        <span>{t("shipping_rules_advanced_summary")}</span>
                    </summary>
                    <div className="admin-integrations-vendors-body">
                    <p className="admin-integrations-field-hint">{t("shipping_rules_advanced_hint")}</p>
                <div className="shipping-rule-grid">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_min_weight_kg")}</label>
                        <input type="number" min={0} step={0.1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.min_weight_grams != null ? rule.conditions.min_weight_grams / 1000 : ""}
                            onBlur={numBlur("min_weight_grams", 1000)} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_max_weight_kg")}</label>
                        <input type="number" min={0} step={0.1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.max_weight_grams != null ? rule.conditions.max_weight_grams / 1000 : ""}
                            onBlur={numBlur("max_weight_grams", 1000)} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_min_boxes")}</label>
                        <input type="number" min={1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.min_boxes ?? ""}
                            onBlur={numBlur("min_boxes")} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_max_boxes")}</label>
                        <input type="number" min={1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.max_boxes ?? ""}
                            onBlur={numBlur("max_boxes")} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_max_longest_side_cm")}</label>
                        <input type="number" min={1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.max_longest_side_cm ?? ""}
                            onBlur={numBlur("max_longest_side_cm")} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_max_length_cm")}</label>
                        <input type="number" min={1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.max_length_cm ?? ""}
                            onBlur={numBlur("max_length_cm")} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_max_width_cm")}</label>
                        <input type="number" min={1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.max_width_cm ?? ""}
                            onBlur={numBlur("max_width_cm")} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_max_height_cm")}</label>
                        <input type="number" min={1} className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={rule.conditions.max_height_cm ?? ""}
                            onBlur={numBlur("max_height_cm")} />
                    </div>
                </div>
                <p className="admin-integrations-field-hint">{t("shipping_rules_weight_bounds_hint")}</p>
                <p className="admin-integrations-field-hint">{t("shipping_rules_dims_hint")}</p>
                    </div>
                </details>

                <div className="admin-integrations-field">
                    <span className="admin-integrations-field-label">{t("shipping_rules_uk_regions")}</span>
                    <p className="admin-integrations-field-hint">{t("shipping_rules_uk_regions_hint")}</p>
                    <div className="admin-integrations-day-chips shipping-rule-region-chips">
                        {UK_REGIONS.map(region => (
                            <button
                                key={region}
                                type="button"
                                className={`admin-integrations-day-chip${(rule.conditions.uk_regions ?? []).includes(region) ? " admin-integrations-day-chip--active" : ""}`}
                                disabled={saving || readOnly}
                                onClick={() => toggleUkRegion(region)}
                            >
                                {t(`region_${region}`)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="shipping-rule-grid">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_sales_channels")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_sales_channels_hint")}</p>
                        <input className="admin-integrations-input" disabled={saving || readOnly}
                            defaultValue={(rule.conditions.sales_channels ?? []).join(", ")}
                            placeholder="Bodysocks, Bodysocks UK"
                            onBlur={(e) => {
                                const next = parseChannels(e.target.value);
                                const curr = rule.conditions.sales_channels ?? [];
                                if (JSON.stringify(next) !== JSON.stringify(curr)) {
                                    patchCondition("sales_channels", next.length > 0 ? next : undefined);
                                }
                            }} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_date_from")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_date_window_hint")}</p>
                        <input type="date" className="admin-integrations-input" disabled={saving || readOnly}
                            value={rule.conditions.date_from ?? ""}
                            max={rule.conditions.date_to ?? undefined}
                            onChange={(e) => patchCondition("date_from", e.target.value || undefined)} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_date_to")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_date_window_hint")}</p>
                        <input type="date" className="admin-integrations-input" disabled={saving || readOnly}
                            value={rule.conditions.date_to ?? ""}
                            min={rule.conditions.date_from ?? undefined}
                            onChange={(e) => patchCondition("date_to", e.target.value || undefined)} />
                    </div>
                </div>

                <div className="shipping-rule-grid">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_countries")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_countries_hint")}</p>
                        <input className="admin-integrations-input admin-integrations-input--mono" disabled={saving || readOnly}
                            defaultValue={(rule.conditions.countries ?? []).join(", ")}
                            onBlur={(e) => {
                                const next = parsePatterns(e.target.value).filter(c => /^[A-Z]{2}$/.test(c));
                                const curr = rule.conditions.countries ?? [];
                                if (JSON.stringify(next) !== JSON.stringify(curr)) {
                                    patchCondition("countries", next.length > 0 ? next : undefined);
                                }
                            }} />
                    </div>
                </div>

                <div className="shipping-rule-grid">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_postcode_includes")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_postcode_hint")}</p>
                        <input className="admin-integrations-input admin-integrations-input--mono" disabled={saving || readOnly}
                            defaultValue={(rule.conditions.postcode_includes ?? []).join(", ")}
                            onBlur={(e) => {
                                const next = parsePatterns(e.target.value);
                                const curr = rule.conditions.postcode_includes ?? [];
                                if (JSON.stringify(next) !== JSON.stringify(curr)) {
                                    patchCondition("postcode_includes", next.length > 0 ? next : undefined);
                                }
                            }} />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("shipping_rules_postcode_excludes")}</label>
                        <p className="admin-integrations-field-hint">{t("shipping_rules_postcode_hint")}</p>
                        <input className="admin-integrations-input admin-integrations-input--mono" disabled={saving || readOnly}
                            defaultValue={(rule.conditions.postcode_excludes ?? []).join(", ")}
                            onBlur={(e) => {
                                const next = parsePatterns(e.target.value);
                                const curr = rule.conditions.postcode_excludes ?? [];
                                if (JSON.stringify(next) !== JSON.stringify(curr)) {
                                    patchCondition("postcode_excludes", next.length > 0 ? next : undefined);
                                }
                            }} />
                    </div>
                </div>

                {error && <p className="admin-integrations-field-error">{error}</p>}

                {!confirmingDelete ? (
                    <button type="button" className="btn btn-sm btn-danger" disabled={saving || readOnly} onClick={() => setConfirmingDelete(true)}>
                        <Trash2 size={14} /> {t("shipping_rules_delete")}
                    </button>
                ) : (
                    <span className="carrier-service-confirm">
                        <span className="admin-integrations-secret-confirm-text">{t("shipping_rules_delete_confirm")}</span>
                        <button type="button" className="btn btn-sm btn-danger" disabled={saving || readOnly} onClick={remove}>
                            {t("shipping_rules_delete")}
                        </button>
                        <button type="button" className="btn btn-sm" disabled={saving || readOnly} onClick={() => setConfirmingDelete(false)}>
                            {t("cancel")}
                        </button>
                    </span>
                )}
            </div>
        </details>
    );
};

const ShippingRulesAdmin = ({ warehouses: allWarehouses }: { warehouses: string[] }) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    // Without cross_warehouse only the person's own warehouse is offered.
    const { canCross, own } = useWarehouseScope();
    const warehouses = useMemo(() => (canCross ? allWarehouses : allWarehouses.filter(w => w === own)), [canCross, own, allWarehouses]);
    const [warehouse, setWarehouse] = useState<string>("");
    const [rules, setRules] = useState<ShippingRule[]>([]);
    const [carriers, setCarriers] = useState<CarrierConfig[]>([]);
    const [warehouseBoxes, setWarehouseBoxes] = useState<WarehouseBox[]>([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [newServiceId, setNewServiceId] = useState<number | "">("");
    const [newStrategy, setNewStrategy] = useState<RuleStrategy>("fixed");
    const [newCandidates, setNewCandidates] = useState<number[]>([]);

    // Dry-run tester state. Boxes are a per-box list (own weight + dims
    // each) so multi-box consignments — and their pricing — are testable;
    // dims are visible inputs because they feed APC's volumetric
    // (chargeable) weight, and a hidden default box was silently
    // overriding the tested weight (Jack hit this 2026-08-05).
    const [testBoxRows, setTestBoxRows] = useState<TesterBox[]>([{ ...DEFAULT_TESTER_BOX }]);
    const [testPostcode, setTestPostcode] = useState("");
    const [testRegion, setTestRegion] = useState("");
    const [testCountry, setTestCountry] = useState("GB");
    const [testDeliveryType, setTestDeliveryType] = useState("standard");
    // Channel + dispatch date for sales_channels / date-window rules; blank
    // = an order with no channel, today.
    const [testChannel, setTestChannel] = useState("");
    const [testDate, setTestDate] = useState("");
    const [testResult, setTestResult] = useState<DryRunResult | null>(null);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        if (!warehouse && warehouses.length > 0) setWarehouse(warehouses[0]);
    }, [warehouses, warehouse]);

    // Services bookable from this warehouse = enabled services on the
    // warehouse's carrier accounts.
    const serviceOptions: ServiceOption[] = useMemo(() => {
        const out: ServiceOption[] = [];
        for (const c of carriers) {
            for (const a of c.accounts) {
                if (a.warehouse !== warehouse) continue;
                for (const s of a.services) {
                    if (!s.enabled) continue;
                    out.push({ ...s, carrierName: c.displayName, accountWarehouse: a.warehouse });
                }
            }
        }
        return out;
    }, [carriers, warehouse]);

    // Tester dropdown options come from what the ENABLED rules actually
    // reference (per Jack, 2026-08-05) — offering a country or region no
    // rule conditions on would just test dead ends. GB is always
    // present: it's the evaluator's default and the APC booking guard.
    const enabledCountries: string[] = useMemo(() => {
        const set = new Set<string>(["GB"]);
        for (const r of rules) {
            if (!r.enabled) continue;
            for (const c of r.conditions.countries ?? []) set.add(c);
        }
        return [...set];
    }, [rules]);

    const enabledRegions: string[] = useMemo(() => {
        const set = new Set<string>();
        for (const r of rules) {
            if (!r.enabled) continue;
            for (const region of r.conditions.uk_regions ?? []) set.add(region);
        }
        return UK_REGIONS.filter(region => set.has(region));
    }, [rules]);

    const load = useCallback(async () => {
        if (!warehouse) return;
        setLoading(true);
        try {
            const [rulesJson, carriersJson, boxesJson] = await Promise.all([
                jsonFetch(`/dispatch-admin/shipping-rules?warehouse=${encodeURIComponent(warehouse)}`),
                jsonFetch(`/admin/carriers`),
                jsonFetch(`/dispatch-admin/boxes?warehouse=${encodeURIComponent(warehouse)}`),
            ]);
            setRules(Array.isArray(rulesJson?.rules) ? rulesJson.rules : []);
            setCarriers(Array.isArray(carriersJson?.carriers) ? carriersJson.carriers : []);
            setWarehouseBoxes(Array.isArray(boxesJson?.boxes) ? boxesJson.boxes : []);
        } catch {
            toast.error(t("shipping_rules_load_failed"), { toastId: "shipping-rules-load-err" });
        } finally {
            setLoading(false);
        }
    }, [warehouse, t]);

    useEffect(() => { load(); }, [load]);

    const handleChanged = (next: ShippingRule) =>
        setRules(curr => curr.map(r => (r.id === next.id ? next : r)));
    const handleDeleted = (id: number) =>
        setRules(curr => curr.filter(r => r.id !== id));

    const move = async (id: number, dir: -1 | 1) => {
        if (readOnly) return;
        const idx = rules.findIndex(r => r.id === id);
        const swap = idx + dir;
        if (idx < 0 || swap < 0 || swap >= rules.length) return;
        const next = [...rules];
        [next[idx], next[swap]] = [next[swap], next[idx]];
        try {
            const json = await jsonFetch(`/dispatch-admin/shipping-rules/reorder`, "POST", {
                warehouse,
                ordered_ids: next.map(r => r.id),
            });
            setRules(Array.isArray(json?.rules) ? json.rules : next);
        } catch {
            toast.error(t("shipping_rules_reorder_failed"), { toastId: "shipping-rules-reorder-err" });
        }
    };

    const canAdd = newName.trim() !== "" && (newStrategy === "cheapest" ? newCandidates.length >= 2 : !!newServiceId) && !readOnly;

    const addRule = async () => {
        if (!canAdd || readOnly) return;
        try {
            const json = await jsonFetch(`/dispatch-admin/shipping-rules`, "POST", {
                warehouse,
                name: newName.trim(),
                strategy: newStrategy,
                ...(newStrategy === "cheapest"
                    ? { candidateServiceIds: newCandidates }
                    : { carrierServiceId: newServiceId }),
                conditions: {},
            });
            setRules(curr => [...curr, json.rule as ShippingRule]);
            setNewName("");
            setNewCandidates([]);
            setNewStrategy("fixed");
            setNewServiceId("");
            setAdding(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : String(err), { toastId: "shipping-rules-add-err" });
        }
    };

    const setBoxField = (idx: number, key: keyof TesterBox, value: string) =>
        setTestBoxRows(rows => rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));

    const runDryRun = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const mm = (v: string, fallback: number) => {
                const n = Math.round(Number(v) * 10);
                return Number.isFinite(n) && n > 0 ? n : fallback;
            };
            const json = await jsonFetch(`/dispatch-admin/shipping-rules/dry-run`, "POST", {
                warehouse,
                delivery_type: testDeliveryType,
                country: testCountry,
                postcode: testPostcode,
                sales_channel: testChannel.trim() || null,
                date: testDate || null,
                boxes: testBoxRows.map(row => ({
                    name: row.name || null,
                    weight_grams: Math.max(1, Math.round(Number(row.weightKg) * 1000) || 1000),
                    // Packaging weight of the picked catalogue box — InPost band
                    // on the measured parcel, so the estimate needs it.
                    tare_grams: warehouseBoxes.find((b) => b.name === row.name)?.weight_grams ?? null,
                    length_mm: mm(row.lengthCm, 300),
                    width_mm: mm(row.widthCm, 200),
                    height_mm: mm(row.heightCm, 100),
                })),
            });
            setTestResult(json as DryRunResult);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : String(err), { toastId: "shipping-rules-test-err" });
        } finally {
            setTesting(false);
        }
    };

    const kg = (grams?: number) => (grams != null ? (grams / 1000).toFixed(2) : "?");

    return (
        <div className="shipping-rules-admin">
            <div className="admin-integrations-field">
                <label className="admin-integrations-field-label">{t("shipping_rules_warehouse")}</label>
                <select
                    className="admin-integrations-input"
                    value={warehouse}
                    onChange={(e) => { setWarehouse(e.target.value); setTestResult(null); }}
                >
                    {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="admin-integrations-loading">
                    <Loader2 size={18} className="admin-integrations-spinner" /> {t("loading")}
                </div>
            ) : (
                <>
                    {serviceOptions.length === 0 && (
                        <p className="admin-integrations-field-hint">{t("shipping_rules_no_services")}</p>
                    )}
                    {serviceOptions.some(isPalletService) && (
                        <p className="admin-integrations-field-hint">{t("shipping_rules_pallet_intro")}</p>
                    )}
                    {rules.length === 0 ? (
                        <p className="admin-integrations-vendors-empty">{t("shipping_rules_empty")}</p>
                    ) : (
                        <div className="shipping-rules-list">
                            {rules.map((rule, idx) => (
                                <RuleRow
                                    key={rule.id}
                                    rule={rule}
                                    services={serviceOptions}
                                    boxNames={warehouseBoxes.map(b => b.name)}
                                    onChanged={handleChanged}
                                    onDeleted={handleDeleted}
                                    onMove={move}
                                    isFirst={idx === 0}
                                    isLast={idx === rules.length - 1}
                                />
                            ))}
                        </div>
                    )}

                    {!adding ? (
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setAdding(true)}
                            disabled={serviceOptions.length === 0 || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            <Plus size={14} /> {t("shipping_rules_add")}
                        </button>
                    ) : (
                        <div className="carrier-service-add-row">
                            <input
                                className="admin-integrations-input carrier-service-name"
                                value={newName}
                                placeholder={t("shipping_rules_name")}
                                onChange={(e) => setNewName(e.target.value)}
                            />
                            <select
                                className="admin-integrations-input carrier-service-type"
                                value={newStrategy}
                                onChange={(e) => setNewStrategy(e.target.value as RuleStrategy)}
                                title={t("shipping_rules_strategy")}
                            >
                                <option value="fixed">{t("shipping_rules_strategy_fixed")}</option>
                                <option value="cheapest" disabled={serviceOptions.filter(s => !isPalletService(s)).length < 2}>{t("shipping_rules_strategy_cheapest")}</option>
                            </select>
                            {newStrategy === "fixed" ? (
                                <select
                                    className="admin-integrations-input carrier-service-type"
                                    value={newServiceId}
                                    onChange={(e) => setNewServiceId(Number(e.target.value) || "")}
                                >
                                    <option value="">{t("shipping_rules_target_service")}</option>
                                    {serviceOptions.map(s => (
                                        <option key={s.id} value={s.id}>{serviceLabel(s, t)}</option>
                                    ))}
                                </select>
                            ) : (
                                <div className="admin-integrations-day-chips">
                                    {serviceOptions.filter(s => !isPalletService(s)).map(s => (
                                        <button
                                            key={s.id}
                                            type="button"
                                            className={`admin-integrations-day-chip${newCandidates.includes(s.id) ? " admin-integrations-day-chip--active" : ""}`}
                                            disabled={readOnly}
                                            onClick={() => setNewCandidates(curr => curr.includes(s.id) ? curr.filter(x => x !== s.id) : [...curr, s.id])}
                                            title={readOnly ? t("read_only_mode") : serviceLabel(s, t)}
                                        >
                                            {s.carrierName} · {s.serviceCode}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <button type="button" className="btn btn-sm btn-primary" onClick={addRule} disabled={!canAdd} title={readOnly ? t("read_only_mode") : undefined}>
                                {t("shipping_rules_add")}
                            </button>
                            <button type="button" className="btn btn-sm" onClick={() => setAdding(false)}>{t("cancel")}</button>
                        </div>
                    )}

                    {/* Inline rule tester — runs the exact evaluator dispatch uses */}
                    <details className="admin-integrations-vendors-details shipping-rules-tester">
                        <summary className="admin-integrations-vendors-summary">
                            <span><FlaskConical size={14} /> {t("shipping_rules_tester_heading")}</span>
                        </summary>
                        <div className="admin-integrations-vendors-body">
                            <p className="admin-integrations-field-hint">{t("shipping_rules_tester_hint")}</p>
                            <div className="shipping-rules-tester-grid">
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("shipping_rules_delivery_types")}</label>
                                    <select className="admin-integrations-input" value={testDeliveryType} onChange={(e) => setTestDeliveryType(e.target.value)}>
                                        {DELIVERY_TYPES.map(d => <option key={d} value={d}>{t(`delivery_type_${d}`)}</option>)}
                                    </select>
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("shipping_rules_tester_country")}</label>
                                    <select className="admin-integrations-input" value={testCountry} onChange={(e) => setTestCountry(e.target.value)}>
                                        {enabledCountries.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                {enabledRegions.length > 0 && (
                                    <div className="admin-integrations-field">
                                        <label className="admin-integrations-field-label">{t("shipping_rules_tester_region")}</label>
                                        <select
                                            className="admin-integrations-input"
                                            value={testRegion}
                                            onChange={(e) => {
                                                const region = e.target.value;
                                                setTestRegion(region);
                                                if (region) setTestPostcode(REGION_EXAMPLE_POSTCODE[region] ?? "");
                                            }}
                                        >
                                            <option value="">{t("shipping_rules_tester_region_any")}</option>
                                            {enabledRegions.map(region => (
                                                <option key={region} value={region}>
                                                    {t(`region_${region}`)} ({REGION_EXAMPLE_POSTCODE[region]})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("shipping_rules_tester_postcode")}</label>
                                    <input className="admin-integrations-input admin-integrations-input--mono" value={testPostcode}
                                        onChange={(e) => { setTestPostcode(e.target.value.toUpperCase()); setTestRegion(""); }}
                                        placeholder="NP19 4RJ" />
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("shipping_rules_tester_channel")}</label>
                                    <input className="admin-integrations-input" value={testChannel}
                                        onChange={(e) => setTestChannel(e.target.value)}
                                        placeholder="Bodysocks UK" />
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("shipping_rules_tester_date")}</label>
                                    <input type="date" className="admin-integrations-input" value={testDate}
                                        onChange={(e) => setTestDate(e.target.value)} />
                                </div>
                            </div>

                            {/* Per-box list: each parcel gets its own weight + dims so
                                multi-box consignments (and their pricing) are testable. */}
                            {testBoxRows.map((row, idx) => (
                                <div key={idx} className="shipping-rules-tester-grid shipping-rules-tester-box-row">
                                    <span className="shipping-rules-tester-box-label">
                                        {t("shipping_rules_tester_box", { n: String(idx + 1) })}
                                    </span>
                                    {warehouseBoxes.length > 0 && (
                                        <div className="admin-integrations-field">
                                            <label className="admin-integrations-field-label">{t("shipping_rules_tester_box_type")}</label>
                                            <select
                                                className="admin-integrations-input"
                                                value={row.name}
                                                onChange={(e) => {
                                                    const name = e.target.value;
                                                    const def = warehouseBoxes.find(b => b.name === name);
                                                    setTestBoxRows(rows => rows.map((r, i) => i !== idx ? r : {
                                                        ...r,
                                                        name,
                                                        // Pre-fill the catalogue box's dims so the
                                                        // volumetric side of the test is realistic.
                                                        ...(def?.length_mm ? { lengthCm: String(def.length_mm / 10) } : {}),
                                                        ...(def?.width_mm ? { widthCm: String(def.width_mm / 10) } : {}),
                                                        ...(def?.height_mm ? { heightCm: String(def.height_mm / 10) } : {}),
                                                    }));
                                                }}
                                            >
                                                <option value="">{t("shipping_rules_tester_box_custom")}</option>
                                                {warehouseBoxes.map(b => (
                                                    <option key={b.name} value={b.name}>{b.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="admin-integrations-field">
                                        <label className="admin-integrations-field-label">{t("shipping_rules_tester_weight")}</label>
                                        <input type="number" min={0.01} step={0.1} className="admin-integrations-input" value={row.weightKg}
                                            onChange={(e) => setBoxField(idx, "weightKg", e.target.value)} />
                                    </div>
                                    <div className="admin-integrations-field">
                                        <label className="admin-integrations-field-label">{t("shipping_rules_tester_length")}</label>
                                        <input type="number" min={1} className="admin-integrations-input" value={row.lengthCm}
                                            onChange={(e) => setBoxField(idx, "lengthCm", e.target.value)} />
                                    </div>
                                    <div className="admin-integrations-field">
                                        <label className="admin-integrations-field-label">{t("shipping_rules_tester_width")}</label>
                                        <input type="number" min={1} className="admin-integrations-input" value={row.widthCm}
                                            onChange={(e) => setBoxField(idx, "widthCm", e.target.value)} />
                                    </div>
                                    <div className="admin-integrations-field">
                                        <label className="admin-integrations-field-label">{t("shipping_rules_tester_height")}</label>
                                        <input type="number" min={1} className="admin-integrations-input" value={row.heightCm}
                                            onChange={(e) => setBoxField(idx, "heightCm", e.target.value)} />
                                    </div>
                                    {testBoxRows.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm shipping-rules-tester-box-remove"
                                            aria-label={t("shipping_rules_tester_remove_box")}
                                            onClick={() => setTestBoxRows(rows => rows.filter((_, i) => i !== idx))}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <div className="shipping-rules-tester-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => setTestBoxRows(rows => [...rows, { ...rows[rows.length - 1] }])}
                                >
                                    <Plus size={14} /> {t("shipping_rules_tester_add_box")}
                                </button>
                                <button type="button" className="btn btn-sm btn-primary" onClick={runDryRun} disabled={testing || !warehouse}>
                                    {testing && <Loader2 size={14} className="admin-integrations-spinner" />}
                                    {t("shipping_rules_tester_run")}
                                </button>
                            </div>
                            {testResult?.facts && (
                                <p className="admin-integrations-field-hint shipping-rules-tester-facts">
                                    {t("shipping_rules_tester_facts", {
                                        boxes: String(testResult.facts.boxCount ?? testBoxRows.length),
                                        actual: kg(testResult.facts.totalWeightGrams),
                                        chargeable: kg(testResult.facts.chargeableWeightGrams),
                                        region: testResult.facts.ukRegion ? t(`region_${testResult.facts.ukRegion}`) : "—",
                                    })}
                                </p>
                            )}
                            {testResult && (
                                testResult.matched ? (
                                    <p className="admin-integrations-test-result admin-integrations-test-result--ok">
                                        {t("shipping_rules_tester_matched", {
                                            rule: testResult.rule?.name ?? "",
                                            service: `${testResult.service?.code ?? ""} — ${testResult.service?.name ?? ""}`,
                                        })}
                                        {testResult.service?.estimatedCost != null && (
                                            <> · {t("shipping_rules_tester_estimated_cost", {
                                                cost: testResult.service.estimatedCost.toFixed(2),
                                            })}</>
                                        )}
                                        {testResult.service?.transit && (
                                            <> · {t("shipping_rules_tester_transit_long", { days: String(testResult.service.transit.extraDays), area: testResult.service.transit.area, when: testResult.service.transit.days })}</>
                                        )}
                                        {(testResult.service?.surcharges ?? []).map((s, i) => (
                                            <span key={i}> · {t("shipping_rules_tester_zone_surcharge", {
                                                cost: s.cost.toFixed(2),
                                                zone: s.label,
                                            })}</span>
                                        ))}
                                        {(testResult.service?.consignments ?? 1) > 1 && (
                                            <> · {t("shipping_rules_tester_consignments", {
                                                n: String(testResult.service?.consignments),
                                            })}</>
                                        )}
                                        {(testResult.candidates ?? []).length > 0 && (
                                            <span className="shipping-rules-candidates">
                                                {(testResult.candidates ?? []).map((c) => (
                                                    <span key={`${c.ruleId ?? 0}-${c.serviceId}`} className={`shipping-rules-candidate${c.serviceId === testResult.service?.id && c.ruleId === testResult.rule?.id ? " shipping-rules-candidate--chosen" : ""}`}>
                                                        {c.ruleName ? `${c.ruleName} › ` : ""}{c.carrier} · {c.serviceCode}: {c.excluded
                                                            ? t("shipping_rules_tester_candidate_excluded", { reason: c.excluded })
                                                            : `£${(c.cost ?? 0).toFixed(2)}${(c.consignments ?? 1) > 1 ? ` (${c.consignments})` : ""}`}
                                                        {c.transit && ` ${t("shipping_rules_tester_transit", { days: String(c.transit.extraDays) })}`}
                                                    </span>
                                                ))}
                                            </span>
                                        )}
                                        {(testResult.facts?.inpostBands ?? []).some(Boolean) && (
                                            <> · {t("shipping_rules_tester_inpost_bands", {
                                                bands: (testResult.facts?.inpostBands ?? []).map((b) => b ?? "XL").join(", "),
                                            })}</>
                                        )}
                                    </p>
                                ) : (
                                    <div className="admin-integrations-test-result admin-integrations-test-result--fail">
                                        <p>{t("shipping_rules_tester_no_match")}</p>
                                        {(testResult.trace ?? []).map(tr => (
                                            <p key={tr.ruleId} className="shipping-rules-trace-line">
                                                {tr.name}: {tr.failed}
                                                {(tr.candidates ?? []).length > 0 && (
                                                    <> — {(tr.candidates ?? []).map((c) => `${c.carrier} · ${c.serviceCode}: ${c.excluded ?? `£${(c.cost ?? 0).toFixed(2)}`}`).join("; ")}</>
                                                )}
                                            </p>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                    </details>
                </>
            )}
        </div>
    );
};

export default ShippingRulesAdmin;
